// The browser client, not the server one: Oak imports modules out of this
// directory directly, so a server-client import here would break its build.
import { DensityUnits, StateOfMatter, UnitType } from "encamp-prisma/generated/browser";

import { hasValue } from "../utils/hasValue";
import {
  CodeOnlyAmountField,
  findCodeWithoutAmount,
  OverridableRfcFields,
  overridableAmountFields,
} from "./applyReportingUnitOverride";
import { MissingDensity } from "./errors";
import { allowedReportingUnitsForOverride } from "./jurisdictionReportingUnits";
import { convert } from "./unitConversions";

/**
 * Why a candidate override unit cannot be honoured, named rather than a bare
 * `false` so a caller can render the actual reason instead of one generic
 * message.
 *
 * `missingDensity` and `noExactAmount` mirror the two conditions
 * `applyReportingUnitOverride` refuses on — see that module's "All or nothing"
 * doc for the full rationale. `jurisdictionDisallowsUnit` is not an
 * aggregation refusal at all: aggregation would apply such an override
 * perfectly happily, and that is exactly the problem. See
 * `canApplyReportingUnitOverride`.
 */
export type CanApplyReportingUnitOverrideResult =
  | { ok: true }
  | { ok: false; reason: "missingDensity" }
  | { ok: false; reason: "noExactAmount"; field: CodeOnlyAmountField }
  | {
      ok: false;
      reason: "jurisdictionDisallowsUnit";
      state: string;
      allowedUnits: UnitType[];
    }
  /** The row already carries an aggregation error, which wins over this one. */
  | { ok: false; reason: "existingAggregationError" }
  /** Change detection skips manually-edited rows, so nothing would persist. */
  | { ok: false; reason: "manuallyEdited" }
  /** No source unit to convert from, but amounts that would need converting. */
  | { ok: false; reason: "unknownSourceUnit" }
  /** No facility state, so the range codes could not be recomputed. */
  | { ok: false; reason: "unknownJurisdiction" }
  /** `convert` failed for a reason other than a missing density. */
  | { ok: false; reason: "notConvertible" };

export type CanApplyReportingUnitOverrideArgs = {
  /**
   * The unit the RFC currently reports in absent any override — the true
   * pre-override unit while one is already active, or the aggregated unit
   * when none has ever applied (see `preOverrideUnit` on
   * `ReportingFacilityChemical`). Using the already-overridden unit here would
   * check convertibility from an already-converted figure, compounding
   * rounding or reversing a dimension change.
   *
   * `null`/`undefined` means there is nothing to convert from yet — a
   * chemical with no RFC row at all, or a row whose unit hasn't been set —
   * so the override cannot fail either precondition.
   */
  fromUnit: UnitType | null | undefined;
  toUnit: UnitType;
  density: number | null | undefined;
  densityUnits: DensityUnits | null | undefined;
  /**
   * The RFC row's amount/code state this override would need to convert (the
   * pre-override figures, by the same reasoning as `fromUnit`, when an
   * override is already active). `null`/`undefined` — no RFC exists yet for
   * this facility + chemical — means there is nothing to misstate, so neither
   * precondition can fail; this is why a chemical with no RFC yet is never
   * wrongly refused.
   */
  rfc: OverridableRfcFields | null | undefined;
  /**
   * The facility's state. It decides which units the jurisdiction accepts, and
   * it selects the range-code table the converted amounts are re-coded against.
   * An unrecognised value means no jurisdiction constraint — the same way the
   * compliance engine's jurisdiction-scoped rules do not fire for a state that
   * matches none of them. Absent entirely is a refusal once an RFC exists,
   * because `applyReportingUnitOverride` refuses there too.
   */
  state: string | null | undefined;
  /**
   * Whether the RFC row has been hand-edited. Change detection returns early
   * for such a row (`checkIfRfcHasChanges`), so aggregation would compute the
   * conversion and then decline to write it — a silent no-op rather than a
   * visible refusal. Refused here so the save fails loudly instead.
   */
  manuallyEdited?: boolean | null;
  /**
   * The chemical's state of matter, needed only for Oregon, whose permitted
   * units depend on it rather than being a fixed list. Absent for an Oregon
   * facility, no unit is permitted — mirroring the compliance engine, which
   * raises an issue on every unit in that case.
   */
  stateOfMatter?: readonly StateOfMatter[] | null;
};

/**
 * Whether a staff reporting-unit override to `toUnit` could actually be
 * honoured. The single definition both the resolver
 * (`setReportingUnitOverride`) and the Oak dialog use, so the units staff are
 * offered and the units the mutation accepts cannot disagree.
 *
 * Most preconditions mirror ones `applyReportingUnitOverride` enforces during
 * aggregation, so an override that saves is one aggregation will apply. The
 * jurisdiction check is the exception and is deliberately *stricter*:
 * aggregation would apply a jurisdiction-illegal override happily, but every
 * state bar CA, OR and SD requires pounds, and the pounds-only portals never
 * read the unit at all — they would file a converted figure into a field
 * labelled pounds. The compliance engine flags it, but only to a customer, on
 * a field only staff can change, and staff bulk-verify skips the engine
 * entirely.
 *
 * Convertibility is delegated to `convert` rather than re-derived from unit
 * dimensions: `canConvertWithoutDensity` disagrees with it for BARRELS, and
 * reusing `convert` keeps that mismatch out of this check. Each field's real
 * amount is probed, not a stand-in, because `convert` short-circuits on a zero
 * amount before it looks at density — a constant would refuse zeroes the real
 * conversion never needs a density for.
 */
export function canApplyReportingUnitOverride({
  fromUnit,
  toUnit,
  density,
  densityUnits,
  rfc,
  state,
  stateOfMatter,
  manuallyEdited,
}: CanApplyReportingUnitOverrideArgs): CanApplyReportingUnitOverrideResult {
  // Checked before the physics, because it is the more fundamental refusal and
  // the more actionable message: "this jurisdiction does not accept gallons"
  // tells staff to stop, where "no catalog density" invites them to go and add
  // one for a unit that still could not be filed. It also applies to a no-op
  // pin to the row's own current unit — an RFC already sitting in a unit its
  // state rejects must not have that unit made permanent.
  const allowedUnits = allowedReportingUnitsForOverride({ state, stateOfMatter });
  if (allowedUnits && !allowedUnits.includes(toUnit)) {
    return {
      ok: false,
      reason: "jurisdictionDisallowsUnit",
      // Non-null by construction: `allowedReportingUnitsForOverride` returns
      // `null` for an absent or unrecognised state, which is the branch above.
      state: state ?? "",
      allowedUnits,
    };
  }

  // Everything below needs a row to reason about. With none, aggregation would
  // apply the override without issue as soon as one exists, and refusing here
  // would make this predicate stricter than the thing it is predicting.
  if (!rfc) {
    return { ok: true };
  }

  // A hand-edited row never gets rewritten, so the conversion would be computed
  // and thrown away. Checked before the physics: whether the numbers *could*
  // convert is moot when the result cannot be persisted.
  if (manuallyEdited) {
    return { ok: false, reason: "manuallyEdited" };
  }

  // Mirrors `applyReportingUnitOverride`, which leaves an existing aggregation
  // error in place and declines the override with `recorded: false` — a
  // log-only refusal invisible to staff. Refusing here is what makes it
  // visible. Not restricted to error skeletons: EHS cross-pollination sets a
  // message on rows that aggregated fully and hold real amounts, and sulfuric
  // acid is an EHS, so this sits squarely on the path the feature was built for.
  if (hasValue(rfc.aggregationErrorMessage)) {
    return { ok: false, reason: "existingAggregationError" };
  }

  // `applyReportingUnitOverride` refuses without a state because it cannot
  // recompute the range codes against a jurisdiction it does not know.
  if (!state) {
    return { ok: false, reason: "unknownJurisdiction" };
  }

  // Checked first among the physics preconditions, and unconditionally on the
  // unit pair, matching
  // `applyReportingUnitOverride`'s own order: a code-only row refuses even a
  // no-op override to its own current unit.
  const codeOnlyField = findCodeWithoutAmount(rfc);
  if (codeOnlyField) {
    return { ok: false, reason: "noExactAmount", field: codeOnlyField };
  }

  // No source unit is only survivable when there is nothing to convert.
  // `applyReportingUnitOverride` passes a null `rfc.unit` straight into
  // `convert`, which throws `CouldNotDetermineUnit` and takes the whole
  // override down, so returning ok here would let staff save an override
  // aggregation refuses on every run.
  if (!fromUnit) {
    const hasAmountToConvert = overridableAmountFields(rfc).some(hasValue);
    return hasAmountToConvert ? { ok: false, reason: "unknownSourceUnit" } : { ok: true };
  }

  if (fromUnit === toUnit) {
    return { ok: true };
  }

  // One `convert` call per field that actually carries a value, on that
  // field's own amount — not a constant — so a null/undefined field is
  // skipped exactly as `applyReportingUnitOverride`'s own `convertAmount`
  // skips it, and a field that is present but `0` reaches `convert`'s own
  // `!amount` short-circuit instead of this loop's density check.
  for (const amount of overridableAmountFields(rfc)) {
    if (!hasValue(amount)) {
      continue;
    }
    try {
      convert(amount, fromUnit, toUnit, density, densityUnits);
    } catch (error) {
      if (error instanceof MissingDensity) {
        return { ok: false, reason: "missingDensity" };
      }
      // Every UnitType value is either Mass or Volume (see
      // `UnitTypeClassification`), so `convert` has no other known failure mode
      // for a valid pair. Returned rather than rethrown because this runs
      // during React render, once per unit, building the override dropdown:
      // a throw here takes the whole dialog down instead of disabling one
      // option.
      return { ok: false, reason: "notConvertible" };
    }
  }

  return { ok: true };
}
